import { Breakpoint, useMediaQuery } from "@stubhub/broadway-providers";
import { AppName, PartnerType } from "src/http/types";
import { NATIONAL_FOOTBALL_TEAMS } from "src/shared/constants/SportsCategories";
import { usePageModelData } from "src/shared/context/PageModelDataContext";
import { CATEGORY_ID_WORLD_SERIES } from "src/website-apps/shared/components/hero-banner/constants";
import { useIsMlb } from "src/website-apps/shared/hooks/useIsMlb";
import { useIsIsWorldCupCategory } from "src/website-apps/shared/hooks/useIsWorldCupCategory";

export enum PerformerLabelType {
  NONE = "NONE",
  MLB = "MLB",
  OFFICIAL_PARTNER = "OFFICIAL_PARTNER",
  FAN_PROTECT_MOBILE = "FAN_PROTECT_MOBILE",
}

export const isPerformerCredibilityBadge = (labelType: PerformerLabelType): boolean =>
  labelType === PerformerLabelType.MLB || labelType === PerformerLabelType.OFFICIAL_PARTNER;

/**
 * Hook to determine which performer credibility label should be displayed.
 * Returns an enum value indicating which label (if any) should be shown.
 *
 * Priority order: MLB \> Official Partner \> None
 */
export const useWillShowPerformerCredibilityLabel = (): PerformerLabelType => {
  const model = usePageModelData();
  const isMlb = useIsMlb();
  const isDesktop = useMediaQuery(Breakpoint.Desktop);

  const isWorldCupCategory = useIsIsWorldCupCategory();

  const showMobileTrustBanner = !isDesktop && model.appName === AppName.Performer;
  const isWorldCupRelatedCategory =
    isWorldCupCategory ||
    (model.appName === AppName.Performer &&
      !!model.categorySummary.categoryId &&
      NATIONAL_FOOTBALL_TEAMS.includes(model.categorySummary.categoryId));
  const isMobileFanProtectBannerVariant = showMobileTrustBanner && isWorldCupRelatedCategory;

  if (model.appName !== AppName.Performer) {
    return PerformerLabelType.NONE;
  }

  if (isMobileFanProtectBannerVariant) {
    return PerformerLabelType.FAN_PROTECT_MOBILE;
  }

  const isWorldSeriesCategory = model.categorySummary.categoryId === CATEGORY_ID_WORLD_SERIES;

  // Check if MLB label should be shown (takes priority)
  if (isMlb || isWorldSeriesCategory) {
    return PerformerLabelType.MLB;
  }

  if (model.categoryMetadata?.sports?.partnerTheme?.partnerType === PartnerType.Partner) {
    return PerformerLabelType.OFFICIAL_PARTNER;
  }

  return PerformerLabelType.NONE;
};
